import { Component, Entity, Object3DSetEvent, registerComponent, registerSystem, System } from "aframe";
import { AnimationAction, AnimationClip,  AnimationMixer, AnimationObjectGroup, fromHalfFloat, KeyframeTrack, LoopOnce, LoopRepeat, Mesh, Object3D, SkinnedMesh } from "three";

/*

"clip" component : define one or more animation clips with a name, loaded from a model (glb)

"clip" system : centralize all clips

*/

export type ClipFilter = (objectName:string, property:string) => boolean;
export type ClipStartCallback = (action : ClipAction) => boolean | void;

export interface ClipSystem extends System {
	clips : { [name : string] : Promise<LoadedClip> };
	playClip(el : Entity, mixer : AnimationMixer, name : string, filter : ClipFilter | null, fadeDuration : number, loop : boolean, beforeStart? : ClipStartCallback) : Promise<ClipAction>;
}


export interface LoadedClip
{
	clip : AnimationClip;
	accessories? : Object3D[];
	hide? : string;
	disableTransform? : string;

	getInstance : (el : Entity, root : Object3D, mirror : boolean, reverse : boolean) => AnimationClip;
}


export interface ClipDefinition
{
	start? : number;
	end? : number;
	name? : string;
	accessories? : string; /* regex : all objects in animation file root that match will be added to the animated character */
	hide? : string; /* selector : all sub-entities of the animated character that match will be hidden during the Clip */
	disableTransform? : string; /* selector : all sub-entities of the animated character that match will be have their "transform" component disabled */
	label? : string; /* UI */
	icon? : string; /* UI */
};

export type ClipDefinitions = { [name:string] : ClipDefinition };


export interface ClipComponent extends Component {
	//data : string[],
	data : ClipDefinitions
	system: ClipSystem;
	loaded: Promise<Object3D>;
	clips : { [name : string] : Promise<LoadedClip> };
}
export type ClipEntity = Entity< { [attr:string]:Component } & { "clip" : ClipComponent } >;


export interface ClipAction
{
	animation : AnimationAction;
	stop(delay : number) : void;
}


const mirrorSide = (side : string) => (side == "Left") ? "Right" : "Left";
const mirrorName = (name : string | null, mirror : boolean) => (name && mirror) ? name.replace( /(Left|Right)/g, mirrorSide ) : name;

const findInScope = (scope : Object3D[], name : string) =>
{
	for(let s of scope)
	{
		if(s.name == name) return s;
		const o = s.getObjectByName(name);
		if(o) return o;
	}
}

const linkRecursive = (obj : Object3D, scope : Object3D[], mirror : boolean) =>
{
	const sm = (obj as SkinnedMesh);
	if(sm && sm.skeleton)
	{
		sm.skeleton.bones = sm.skeleton.bones.map( (bone) => (findInScope(scope, mirrorName(bone.name, mirror)! ) as any) || bone );
	}
	for(let child of obj.children)
		linkRecursive(child, scope, mirror);
}
const linkAccessory = (obj : Object3D & { sourceObject? : Object3D }, scope : Object3D[], mirror : boolean ) =>
{
	const sourceParent = (obj.sourceObject && obj.sourceObject.parent) || obj.parent;
	const parentName = mirrorName(sourceParent && sourceParent.name, mirror);
	if(parentName) //Attach to an object with the same name in scope
	{
		const p = findInScope(scope, parentName);
		if(p)
			p.add(obj);
		//else ???
	}

	linkRecursive(obj, scope, mirror);
}

registerSystem("clip", {
	
	init(this:ClipSystem)
	{
		this.clips = {};
	},

	async playClip(this:ClipSystem, el : Entity, mixer : AnimationMixer, name : string, trackFilter : ClipFilter | null, fadeDuration : number, loop : boolean, beforeStart? : ClipStartCallback )
	{
		let baseName : string = name, mirror : boolean = false, reverse : boolean = false;

		while(baseName.length)
		{
			if(baseName.startsWith("~"))
			{
				mirror = true;
				baseName = baseName.slice(1);
			}
			else if(baseName.startsWith("-"))
			{
				reverse = true;
				baseName = baseName.slice(1);
			}
			else break;
		}

		const mName = mirrorName(baseName, mirror)!;

		let clip : Promise<LoadedClip> | undefined = undefined;

		//First, look for a local clip on target entity
		if(el && el.components)
		{
			for(let key in el.components)
			{
				const clipComp = el.components[key] as ClipComponent;
				if(clipComp && (clipComp.name == "clip") && clipComp.clips)
				{
					clip = clipComp.clips[ mName ] || clipComp.clips[ baseName ];
					if(clip !== undefined)
						break;
				}
			}
		}

		//Then, look for a global registered clip
		if(!clip)
		{
			clip = this.clips[mName] || this.clips[baseName];

			if(!clip)
			{
				if(!baseName) return;
				console.warn("No animation", name, baseName, mName);
				clip = this.clips[""];
				if(!clip) return;
			}
		}

		
		
		const loadedClip = await clip;
				
		const animClip = loadedClip.getInstance(el, el.object3D, mirror, reverse);

		const clipInstance = new AnimationClip(name, animClip.duration, animClip.tracks.slice(), animClip.blendMode);
		clipInstance.uuid = name;
		for(let n = trackFilter ? clipInstance.tracks.length : 0; --n >= 0; )
		{
			const trackName = clipInstance.tracks[n].name;
			const ix = trackName.lastIndexOf(".");
			const objectName = trackName.slice(0, ix), propertyName = trackName.slice(ix+1);
			
			if(trackFilter!(objectName, propertyName))
				continue; //The filter said the track must be kept.

			if( loadedClip.accessories )
			{
				let accIx = loadedClip.accessories.length;
				while(--accIx >= 0)
				{
					const acc = loadedClip.accessories[accIx];
					if(acc.name == objectName) break;
					if(acc.getObjectByName(objectName)) break;
				}

				if(accIx >= 0) //Track of an accessory
					continue; //Don't filter.
			}

			clipInstance.tracks.splice(n, 1);
			clipInstance.uuid += "!" + trackName;
		}



		let action = mixer.existingAction( clipInstance ) as AnimationAction & { 
			accessories? : Object3D[], 
			hiddenEntities? : Entity[], 
			disabledTransforms? : Entity[],
			playing? : boolean 
		};

	
		// Create accessories instances BEFORE starting the action	
		const accessories : Object3D[] = [];
		if(loadedClip.accessories)
		{
			const actionAccessories = action && action.accessories;
			if(actionAccessories && (loadedClip.accessories.length == actionAccessories.length))
			{
				let n = actionAccessories.length;
				while(--n >= 0)
				{
					if( (actionAccessories[n] as any).sourceObject !== loadedClip.accessories[n] )
						break;
				}
				if(n < 0) //All accessories already good
					accessories.push( ...actionAccessories );
			}

			if(accessories.length)
			{
				const scope : Object3D[] = [ ];

				for(let key in el.object3DMap)
					scope.push( el.object3DMap[key] );

				for(let obj of accessories)
					linkAccessory(obj, scope, mirror);
			}
			else
			{
				//Accessories are no longer good (not sure why, but anyway...), destroy them
				if(actionAccessories)
				{
					for(let obj of actionAccessories)
						obj.removeFromParent();

					delete action.accessories;
				}


				for(let obj of loadedClip.accessories)
				{
					const clone = obj.clone(true);
					(clone as any).sourceObject = obj;
					accessories.push(clone);
				}
				
				const scope : Object3D[] = [ ];
				
				// Look for link target in el.object3DMap instead of el.object3D :
				for(let key in el.object3DMap)
					scope.push( el.object3DMap[key] );
					
				// If not found in el, try to link to other accessories :
				scope.push(...accessories);

				for(let obj of accessories)
					linkAccessory(obj as any, scope, mirror);
			}
		}


		const hiddenEntities : Entity[] = [];
		const disabledTransforms : Entity[] = [];
		if( loadedClip.hide )
		{
			if(action && action.hiddenEntities)
			{
				for(let entity of action.hiddenEntities)
					if(entity.object3D && !entity.object3D.visible)
					{
						entity.object3D.visible = true; //Will probably be set back to false just below
						entity.emit("visible-changed", true, false);
					}
			}
			const selector = mirrorName(loadedClip.hide, mirror)!;
			Array.prototype.push.apply(hiddenEntities, el.querySelectorAll<Entity>(selector) as any );
		}

		if(loadedClip.disableTransform)
		{
			if(action && action.disabledTransforms)
			{
				for(let entity of action.disabledTransforms)
					if(entity.components && entity.components.transform)
						entity.setAttribute("transform", { enabled : true });
			}
			const selector = mirrorName(loadedClip.disableTransform, mirror)!;
			Array.prototype.push.apply(disabledTransforms, el.querySelectorAll<Entity>(selector) as any );
		}

		

		if(!action)
		{
			action = mixer.clipAction( clipInstance ) as any;
		}


		const result : ClipAction = {
			animation : action,
			stop : (delay : number) =>
			{
				if(delay > 0)
					action.fadeOut(delay);
				else
					action.stop().enabled = false;
				action.playing = false;
				//Don't delete accessories : keep the instances for next time.
				//delete action.accessories;
				delete action.hiddenEntities;
				delete action.disabledTransforms;

				for(let entity of hiddenEntities)
					if(entity.object3D && !entity.object3D.visible)
					{
						entity.object3D.visible = true;
						entity.emit("visible-changed", true, false);
					}

				for(let entity of disabledTransforms)
					if(entity.components && entity.components.transform)
						entity.setAttribute("transform", { enabled : true });
			
				for(let obj of accessories)
					obj.removeFromParent();
			}
		};

		action.accessories = accessories;
		action.hiddenEntities = hiddenEntities;
		action.disabledTransforms = disabledTransforms;

		if( beforeStart )
			if( beforeStart(result) === false )
			{
				result.stop(0);
				return;
			}

		for(let entity of hiddenEntities)
			if(entity.object3D && entity.object3D.visible)
			{
				entity.object3D.visible = false;
				entity.emit("visible-changed", false, false );
			}
		for(let entity of disabledTransforms)
			if(entity.components && entity.components.transform)
				entity.setAttribute("transform", { enabled : false });
		
		action.enabled = true;
		action.clampWhenFinished = !loop;
		if(loop)
			action.setLoop( LoopRepeat , Infinity );
		else
			action.setLoop( LoopOnce , 0 );

		if(!action.playing)
		{
			action.playing = true;
			action.reset();
			action.fadeIn(fadeDuration);
			action.play();
		}

		mixer.update(0);

		return result;
	}
});




const makeInstance = (sourceClip : LoadedClipImpl, mirror : boolean, reverse : boolean, scales? : { [name:string] : number }, morphs? : { [name:string] : MorphTargetDictionary } ) =>
{
	const source = sourceClip.clip;
	const result = source.clone();

	//Discard morphTargetInfluences tracks. We'll build target-specific tracks.
	result.tracks = result.tracks.filter( trk => !trk.name.endsWith(".morphTargetInfluences") );

	//Play from end to start
	if(reverse)
	{
		for(let track of result.tracks)
		{
			const vsize = track.getValueSize();
			const rtimes = new Float32Array( track.times.length ), rvalues = new Float32Array( track.values.length );		
			for(let src = track.times.length, dst = 0; --src >= 0; ++dst )
			{
				rtimes[dst] = source.duration - track.times[src];
				for(let v=vsize; --v >= 0; )
					rvalues[vsize * dst + v] = track.values[vsize * src + v];
			}
			track.times = rtimes;
			track.values = rvalues;
		}
	}

	//Swap left/right along X axis
	if(mirror)
	{
		for(let track of result.tracks)
		{
			if(track.name.startsWith("Left")) track.name = "Right" + track.name.slice(4);
			else if(track.name.startsWith("Right")) track.name = "Left" + track.name.slice(5);
	
			if(track.name.endsWith(".position"))
			{
				for(let ix = 0; ix < track.values.length; ix += 3 )
					track.values[ix] = -track.values[ix];
			}
			else if(mirror && track.name.endsWith(".quaternion"))
			{
				for(let ix = 0; ix < track.values.length; ix += 4 )
				{
					track.values[ix+1] = -track.values[ix+1];
					track.values[ix+2] = -track.values[ix+2];
				}
			}
		}
	}

	
	if(morphs && sourceClip.morphTargetTracks)
	{
		for(let morphObjectName in morphs)
		{
			const morphObjectDictionary = morphs[ morphObjectName ];

			//Try to find a morph track in source clip that has common morph targets with target morph object :
			for(let sourceTrackName in sourceClip.morphTargetTracks)
			{
				const trackMorphTargets : string[] = sourceClip.morphTargetTracks[sourceTrackName];
				const sourceMorphCount = trackMorphTargets.length;

				let sourceTrack : KeyframeTrack | undefined = undefined;
				let resultTrack : KeyframeTrack | undefined = undefined;
				let targetMorphCount = 0;


				for(let sourceMorphIndex = 0; sourceMorphIndex < sourceMorphCount; ++sourceMorphIndex )
				{
					const morphTargetName = trackMorphTargets[sourceMorphIndex];
					const morphTargetIndex = morphObjectDictionary[morphTargetName];
					if(morphTargetIndex === undefined) continue;

					if(!(resultTrack && sourceTrack))
					{
						sourceTrack = source.tracks.find( trk => trk.name == sourceTrackName );
						if(!sourceTrack)
							break; //WTF

						for(let _ in morphObjectDictionary)
							++targetMorphCount;
	
						resultTrack = new KeyframeTrack(morphObjectName + ".morphTargetInfluences",
							sourceTrack.times,
							new Float32Array( targetMorphCount * sourceTrack.times.length ),
							sourceTrack.getInterpolation());
					}

					for(let keyIndex = 0; keyIndex < resultTrack.times.length; ++keyIndex)
						resultTrack.values[ morphTargetIndex + keyIndex * targetMorphCount ] = sourceTrack.values[ sourceMorphIndex + keyIndex * sourceMorphCount ]
				}

				if(resultTrack)
				{
					result.tracks.push(resultTrack);
					break;
				}
			}
		}
	}


	if(scales)
	{
		for(let _any in scales)
		{
			for(let track of result.tracks)
			{
				if(!track.name.endsWith(".position"))
					continue;
				
				const targetName = track.name.slice(0, track.name.lastIndexOf("."));
				const scale = scales[targetName];
				if(scale > 0.0)
				{
					for(let ix = 0; ix < track.values.length; ++ix )
						track.values[ix] = scale * track.values[ix];
				}
			}
			break;
		}
	}


	return result;
}


type MorphTargetDictionary = { [target:string] : number };
const morphTargetDictionaryToArray = (dic : MorphTargetDictionary) =>
{
	const targetNames : string[] = [];
	for(let key in dic)
		targetNames[ dic[key] ] = key;
	return targetNames;
}

const enumerateMorphObjects = (el : Entity, from : Object3D & { el? : Entity, morphTargetDictionary? : MorphTargetDictionary }  , to : { [name : string] : MorphTargetDictionary }) =>
{
	if(from.el && (from.el !== el)) return;

	if(from.morphTargetDictionary)
		to[ from.name ] = from.morphTargetDictionary;

	for(let child of from.children)
		enumerateMorphObjects(el, child, to);
}


const findObject3D = (el : Entity, name: string, from : Object3D & { el? : Entity } ) : Object3D | undefined =>
{
	if(from.el && (from.el !== el)) return;
	if(from.name == name) return from;
	
	for(let child of from.children)
	{
		let found = findObject3D(el, name, child);
		if(found) return found;
	}
}


const parseAccessories = (obj : Object3D, regex : RegExp, result : Object3D[] ) =>
{
	if(obj.name && regex.test(obj.name))
	{
		result.push(obj);
	}
	else // Don't add children if parent is already added (parent will be cloned with children)
	{
		for(let child of obj.children)
			parseAccessories(child, regex, result);
	}
}


class LoadedClipImpl implements LoadedClip
{
	cache : { [name:string] : AnimationClip }
	positionTargets? : { [name:string] : number };
	morphTargetTracks? : { [name:string] : string[] /* ordered list of morph target names */ };
	clip:AnimationClip;
	accessories?: Object3D[];
	hide? : string; //selector
	disableTransform? : string;
	

	constructor(root : Object3D, clipDef : ClipDefinition)
	{
		this.cache = {};

		const name = clipDef.name;
		let clip = name ? root.animations.find( c => c.name == name ) : root.animations[0];
		if(!clip)
			throw name;

		const start = Number(clipDef.start), end = Number(clipDef.end);
		if(!isNaN(start) || !isNaN(end))
		{
			clip = clip.clone();
			clip.tracks = clip.tracks.map( (track) => 
			{
				let newTrack = track.trim(start, end);
				if(!isNaN(start))
					newTrack = newTrack.shift(-start);
				return newTrack;
			} );
			if(isNaN(start)) clip.duration = end;
			else if(isNaN(end)) clip.duration -= start;
			else clip.duration = end - start;
		}

		this.clip = clip;

		this.hide = clipDef.hide;
		this.disableTransform = clipDef.disableTransform;

		if( clipDef.accessories )
		{
			const rx = new RegExp(clipDef.accessories);
			this.accessories = [];
			parseAccessories(root, rx, this.accessories);
		}
		
		for(let track of this.clip.tracks)
		{
			if(track.name.endsWith(".position"))
			{
				const targetName = track.name.slice(0, track.name.lastIndexOf("."));
				const sourceObject = root.getObjectByName(targetName);
				if(sourceObject)
				{
					if(!this.positionTargets)
						this.positionTargets = {};
					this.positionTargets[ targetName ] = sourceObject.position.lengthSq();
				}
			}
			else if(track.name.endsWith(".morphTargetInfluences"))
			{
				const targetName = track.name.slice(0, track.name.lastIndexOf("."));
				const sourceObject = root.getObjectByName(targetName) as Object3D & { morphTargetDictionary? : MorphTargetDictionary  };
				if(sourceObject && sourceObject.morphTargetDictionary)
				{
					if(!this.morphTargetTracks)
						this.morphTargetTracks = {};
					this.morphTargetTracks[ track.name ] = morphTargetDictionaryToArray(sourceObject.morphTargetDictionary);
				}

			}
		}
	}

	getInstance( el : Entity, root : Object3D,  mirror :boolean, reverse:boolean ) : AnimationClip
	{
		let scales : { [name:string] : number } | undefined = undefined;
		let morphs : { [name:string] : MorphTargetDictionary } | undefined = undefined;
		
		let cacheName = "";
		if(mirror) cacheName += "mirror;";
		if(reverse) cacheName += "reverse;";
		
		if(root) //Build an AnimationClip instance for this specific target
		{
			if( this.positionTargets )
			{
				scales = {};
				for(let key in this.positionTargets)
				{
					const targetObj = findObject3D(el, key, root);
					if(!targetObj) continue;

					for(let p : Object3D | null = targetObj; p && (p !== el.object3D); p = p.parent)
					{
						// HACK : this only works with models created by model-instance
						const srcRoot = ( p as Object3D & { sourceObject? : Object3D } ).sourceObject;
						if(!srcRoot)
							continue;

						const srcObject = srcRoot.getObjectByName(key);
						if(srcObject)
						{
							const scale = Math.sqrt( srcObject.position.lengthSq() / this.positionTargets[key] );
							if((scale > 0) && (scale != 1.0))
								scales[key] = scale;
							break;
						}
					}
				}

				const scaleObjectNames : string[] = [];
				for(let objectName in scales)
					scaleObjectNames.push(objectName);
				
				if(scaleObjectNames.length)
				{
					cacheName += "scale:";
					for(let objectName of scaleObjectNames.sort())
						cacheName += objectName+"["+scales[objectName]+"]";
					cacheName += ";";
				}

			}

			if(this.morphTargetTracks)
			{
				morphs = {};
				enumerateMorphObjects( el, root, morphs );

				const morphObjectNames : string[] = [];
				for(let objectName in morphs)
					morphObjectNames.push(objectName);
				if(morphObjectNames.length)
				{
					cacheName += "morph:";
					for(let objectName of morphObjectNames.sort())
						cacheName += objectName+"["+  morphTargetDictionaryToArray(morphs[objectName]).join(",") +"]";
					cacheName += ";";
				}
			}

		}

		const inCache = this.cache[cacheName];
		return inCache || ( this.cache[cacheName] = makeInstance(this, mirror, reverse, scales, morphs) );
	}


}



// Name : [start_time] .. [end_time]
//const clipRegExp = /^([^:]+):([^\[]+)?(\-?\d*(?:\.\d*)?)\s*\.\.\s*(\-?\d*(?:\.\d*)?)\s*$/


// Name : {animation_name}[{start_time}..{end_time}]
const clipRegExp = /^([^:]+):([^[]+)?(?:\[(\-?\d*(?:\.\d*)?)\.\.(\-?\d*(?:\.\d*)?)\])?$/

const valueRegExp = /^(?<name>[^[]+)?(?:\[(?<start>\-?\d*(?:\.\d*)?)\.\.(?<end>\-?\d*(?:\.\d*)?)\])?/;

const parseClip = (value : any) =>
{
	if(!value) return {};

	const result : ClipDefinitions = {};

	if(value.constructor === Object)
	{
		for(let key in value)
		{
			const keyValue = value[key];
			if(keyValue && (keyValue.constructor === Object))
			{
				result[ key ] = { 
					start : keyValue.start as number, 
					end : keyValue.end as number,
					name : ('name' in keyValue) ? (keyValue.name as string) : key, //Omitted = "same as key"
					hide : keyValue.hide as string,
					disableTransform : keyValue.disableTransform as string,
					accessories : keyValue.accessories as string,
					label : keyValue.label as string,
					icon : keyValue.icon as string
				};
			}
			else if( (typeof keyValue === "string") || (keyValue instanceof String) )
			{
				const name = String(keyValue);
				const match = name.match(valueRegExp);
				if(match && match.groups)
				{
					result[ key ] = { 
						name : match.groups.name || key,
						start : match.groups.start ? Number(match.groups.start) : undefined,
						end : match.groups.end ? Number(match.groups.end) : undefined
					};
				}
				else
					result[ key ] = { name : (name || key) }; //Empty name means "same as key"
			}
			else
			{
				//TODO : parse
				console.warn("Invalid clip value :", keyValue);
			}
		}
		return result;
	}

	if(!Array.isArray(value))
		value = String(value).split( /[,;]/ );

	for(let item of value as string[])
	{
		const match = clipRegExp.exec(item);
		let key : string = "", value : ClipDefinition = {};
		if(match)
		{
			key = match[1].trim();
			value.name = match[2].trim();
			value.start = parseFloat(match[3]);
			value.end = parseFloat(match[4]);
		}
		else
		{
			key = item.trim();
			value.name = key;
		}
		if(key)
			result[key] = value;
	}

	return result;
};

const stringifyClip = (value : any) =>
{
	if(!value) return "";

	if(value.constructor !== Object) return String(value);

	let str = "", sep = "";
	for(let key in value)
	{
		str += sep + key;
		sep = ",";
		const range = value[key];
		if(!range) continue;
		const start = range.start, end = range.end;
		if(isNaN(start) && isNaN(end)) continue;
		str += ":" + (isNaN(start) ? '' : String(start)) + ".." + (isNaN(end) ? '' : String(end));
	}
	return str;
};


const loadClip = async (loaded : Promise<Object3D>, clipDef : ClipDefinition ) =>
{
	const rootObject = await loaded;
	return new LoadedClipImpl(rootObject, clipDef);
};


registerComponent("clip", {
	
	schema : {
		default : {},
		//type : "array", detault : []
		parse : parseClip,
		stringify : stringifyClip
	},

	multiple : true, // A little bit of hack : clip components with an ID (clip__id) are NOT registered globally

	init(this : ClipComponent)
	{
		this.clips = {};

		this.loaded = new Promise( (resolve, reject) =>
		{
			for(let key in this.el.object3DMap)
			{
				const obj = this.el.object3DMap[key];
				if(obj && obj.animations && obj.animations.length)
					return resolve(obj);
			}

			const listener = (event : Object3DSetEvent) =>
			{
				if(event.target !== this.el) return;
				const obj = event.detail.object;
				if(obj && obj.animations && obj.animations.length)
				{
					this.el.removeEventListener("object3dset", listener as EventListener);
					resolve(obj);
				}
			}
			this.el.addEventListener("object3dset", listener);
		} );

		this.loaded.then( () => this.el.emit("clip-loaded", this, false));
	},
	update(this : ClipComponent, oldData : ClipDefinition)
	{
		if(oldData)
			for(let name in oldData)
			{
				delete this.clips[name];
				if(!this.id)
					delete this.system.clips[name];
			}
				
		for(let name in this.data)
		{
			const promise = loadClip(this.loaded, this.data[name] );
			this.clips[name] = promise;
			if(!this.id) // Not a local clip component
				this.system.clips[name] = promise;
		}
	},
	remove(this : ClipComponent) 
	{ 
		for(let name in this.data)
		{
			delete this.clips[name];
			if(!this.id)
				delete this.system.clips[name];
		}

	}
});
